/**
 * The scoring of the portfolios' composition (portal PR 10), as the evaluator
 * and the team see it. The server decides — the bounds, the totals, the mean,
 * admitted or refused; this mirrors its rules so the evaluator sees, while
 * typing, what a 14 out of 20 means.
 *
 * Points are kept as strings and added in hundredths, as integers: never as
 * floats, which would turn 69.995 into something else.
 */

/** "14,5" → 1450 hundredths; null if it is not a valid number of points. */
export function hundredths(raw) {
  const s = String(raw ?? "").trim().replace(",", ".");
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(s)) return null;
  const [int, dec = ""] = s.split(".");
  return Number(int) * 100 + Number(dec.padEnd(2, "0"));
}

/** What is wrong with one criterion's points, or "" when they are fine. */
export function pointProblem(raw, max) {
  if (String(raw ?? "").trim() === "") return "Scrie punctajul.";
  if (/^-\s*\d/.test(String(raw).trim())) return `Punctajul e între 0 și ${max}.`;
  const h = hundredths(raw);
  if (h === null) {
    return /[.,]\d{3,}/.test(String(raw)) ? "Cel mult două zecimale." : "Punctajul e un număr.";
  }
  if (h > max * 100) return `Punctajul e între 0 și ${max}.`;
  return "";
}

/** "Foarte bine" from 90%, "Bine" from 70%, below that the third level. */
export function levelOf(raw, max, levels) {
  const h = hundredths(raw);
  if (h === null || h > max * 100) return null;
  // Compare in integers: h / (max·100) ≥ from  ⇔  h ≥ from·max·100.
  return levels.find((lv) => h >= Math.round(lv.from * max * 100)) || null;
}

/** The sum of the criteria, as "86,00"; "" while one is missing or wrong. */
export function totalOf(points, criteria) {
  let sum = 0;
  for (const c of criteria) {
    const raw = points[String(c.n)];
    if (pointProblem(raw, c.max)) return "";
    sum += hundredths(raw);
  }
  return showHundredths(sum);
}

export function showHundredths(h) {
  return `${Math.floor(h / 100)},${String(h % 100).padStart(2, "0")}`;
}

/** The server's exact decimal ("69.495") shown the annex's way: truncated, comma. */
export function showExact(value) {
  if (value === null || value === undefined || value === "") return "";
  const [int, dec = ""] = String(value).split(".");
  return `${int},${dec.padEnd(2, "0").slice(0, 2)}`;
}

/** What changes between the two rounds on the same grid (PR 11a). */
export const ROUND_LABELS = {
  composition: {
    tab: "Compoziția", title: "Portofoliile admise la structură", individual: "8", register: "9",
    closed: "Evaluarea se deschide după ce formatorii generează Anexa 6: din ea se știe cine e admis.",
    nobody: "Nimeni nu e admis la verificarea structurii: nu are cine fi punctat.",
    rule: "procedura, art. 28 alin. 1",
  },
  final: {
    tab: "Susținerea", title: "Cursanții prezenți la susținere", individual: "11", register: "12",
    closed: "Punctarea susținerii se deschide după ce echipa confirmă prezența și generează Anexa 10.",
    nobody: "Nimeni nu e trecut prezent în Anexa 10: nu are cine fi punctat.",
    rule: "procedura, art. 30",
  },
};

/** The evaluator's line at the top of their sheet. */
export function sheetHeadline(data) {
  if (!data) return "";
  const L = ROUND_LABELS[data.round || "composition"];
  if (!(data.basis || data.annex_6)) return L.closed;
  if (!data.rows.length) return L.nobody;
  if (data.sheet.status === "signed") {
    return data.all_signed
      ? "Fișa ta e semnată, la fel ca ale celorlalți evaluatori: mediile sunt mai jos."
      : "Fișa ta e semnată. Mediile apar după ce semnează toți evaluatorii.";
  }
  const left = data.rows.filter((r) => !r.score).length;
  if (data.sheet.status === "reopened") return "Echipa ți-a redeschis fișa: corectează și semnează din nou.";
  if (left) return `Mai ai de punctat ${left === 1 ? "un portofoliu" : `${left} portofolii`} din ${data.rows.length}.`;
  return "Ai punctat toate portofoliile. Mai rămâne să semnezi fișa.";
}

/** When the round happens, for the line under the title. */
export function roundWhen(data, longDate) {
  const s = data?.session;
  if (!s) return "";
  if ((data.round || "composition") === "composition") return `Termen: până pe ${longDate(s.composition_due)}`;
  return s.final_date ? `Susținerea: ${longDate(s.final_date)}` : `Susținerea: între ${longDate(s.final_from)} și ${longDate(s.final_to)}`;
}

export const SHEET_STATES = {
  draft: { label: "În lucru", tone: "info" },
  reopened: { label: "Redeschisă", tone: "warn" },
  signed: { label: "Semnată", tone: "good" },
};

export function minutesLabel(m) {
  if (m === null || m === undefined) return "";
  if (m < 1) return "sub un minut";
  return m < 60 ? `${Math.round(m)} min` : `${Math.floor(m / 60)} h ${Math.round(m % 60)} min`;
}

/** "Admis" / "Respins" / "—" for one of the three conditions. */
export function conditionLabel(v) {
  if (v === true || v === "Admis" || v === "Promovat") return { label: "Da", tone: "good" };
  if (v === false || v === "Respins" || v === "Nepromovat") return { label: "Nu", tone: "late" };
  return { label: "—", tone: "neutral" };
}
